"use client"
import { Button, Typography } from "@mui/material"
import { CarModelStripDto } from "api"
import { trackModelsPageClicks } from "common/analytics"
import useShopUrl from "common/bit/hooks/useShopUrl"
import NextImage from "common/bit/NextImage"
import NextLink from "common/bit/NextLink"
import slugify from "common/bit/utils/slugify"
import { useIsDesktop } from "common/MediaQueries"
import PreSaleTag from "common/PreSaleTag"
import styled from "styled-components"
import { StyledCol, StyledRow, TypographyRtl } from "theme/shared"

const StyledWrapper = styled.div<{ $futureModel?: boolean; $inView?: boolean }>`
  display: flex;
  ${(p) => p.theme.breakpoints.down("sm")} {
    padding: ${(p) => (p.$futureModel ? "20px 10px" : "20px")};
    flex-direction: row;
    align-items: center;
    gap: 20px;
  }
  ${(p) => p.theme.breakpoints.up("md")} {
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    width: 267px;
    min-height: 275px;
    margin-bottom: 70px;

    transform: ${({ $inView }) =>
      !$inView ? "translateY(-50px)" : "translateX(0)"};
    opacity: ${({ $inView }) => ($inView ? "1" : "0")};

    transition: all 0.9s 0.9s cubic-bezier(0.19, 0.88, 0.53, 1.12);
    transition-delay: 0.3s;
  }
`
const StyledTitle = styled(TypographyRtl).attrs({ variant: "h3" })<{
  $left?: boolean
}>`
  text-align: ${(p) => (p.$left ? "left" : "auto")};
  ${(p) => p.theme.breakpoints.up("md")} {
    text-align: center;
    font-weight: normal;
    font-size: 24px;
    white-space: nowrap;
  }
`
const StyledAnnouncement = styled(TypographyRtl)`
  font-size: 12px;
  color: ${(p) => p.theme.colors.mazdaRed};
  margin-bottom: 0;
  text-align: right;
  line-height: 1;
  ${(p) => p.theme.breakpoints.up("md")} {
    text-align: center;
  }
`
const StyledPrice = styled(TypographyRtl).attrs({ variant: "body1" })<{
  $left?: boolean
}>`
  text-align: ${(p) => (p.$left ? "left" : "auto")};
  ${(p) => p.theme.breakpoints.up("md")} {
    text-align: center;
  }
`
const StyledLicenseFee = styled(TypographyRtl).attrs({ variant: "body2" })<{
  $left?: boolean
}>`
  text-align: ${(p) => (p.$left ? "left" : "auto")};

  font-size: ${(p) => (p.$left ? "10px" : "14px")};
  color: ${(p) => p.theme.colors.onyx};
  width: 200px;
  height: 40px;
  ${(p) => p.theme.breakpoints.up("md")} {
    text-align: center;
    font-size: 14px;
    margin: 0px auto;
    width: auto;
  }
`
const StyledButtonRow = styled(StyledRow)`
  gap: 30px;
  ${(p) => p.theme.breakpoints.up("md")} {
    width: 100%;
    justify-content: center;
    margin-top: 10px;
  }
`
const StyledButton = styled(Button).attrs({ variant: "contained" })`
  ${(p) => p.theme.breakpoints.down("sm")} {
    font-size: 16px;
  }
`
const StyledLinkBox = styled(NextLink)`
  cursor: pointer;
  width: 100%;
  height: 100%;
  display: block;
  ${(p) => p.theme.breakpoints.down("sm")} {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 115px;
  }
`

const StyledImageContainer = styled.div`
  aspect-ratio: 500/303;
  width: 100%;
  ${(p) => p.theme.breakpoints.down("sm")} {
    .ModelCardNextImage {
      height: unset;
      aspect-ratio: 1 / 1;
    }
  }

  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-left: 20px;
  }
`

const StyledPreSaleTagWrapper = styled.div`
  margin: 0 auto;
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin: unset;
  }
`

export type modelCardType = "shop" | "finance" | "main"

type ModelCardProps = {
  modelData: CarModelStripDto
  type?: modelCardType
  inView?: boolean
}

const ModelCard = ({ modelData, type = "main", inView }: ModelCardProps) => {
  const isFinance = type === "finance"
  const isshop = type === "shop"
  const isDesktop = useIsDesktop()
  const futureModel = modelData.carState === "Future"
  const carLink = `/model/[modelId]/[modelSlug]`
    .replace("[modelId]", modelData.id.toString())
    .replace("[modelSlug]", slugify(modelData.name))
  const shopUrl = useShopUrl(modelData.id)
  const isInShop = modelData.isInShop
  const isPreSale = modelData.carState === "PreSale"

  const linkUrl = isshop
    ? shopUrl
    : isFinance
    ? modelData?.link
      ? modelData?.link + "?openPopup=true"
      : carLink + "?openPopup=true"
    : modelData?.link || carLink

  return (
    <StyledWrapper $inView={inView} $futureModel={futureModel}>
      <StyledLinkBox href={linkUrl}>
        <StyledImageContainer onClick={() => trackModelsPageClicks()}>
          <NextImage
            image={modelData?.image}
            cover={false}
            imageSizes={["150px", "267px"]}
            alt={modelData?.name}
            containerClassName="ModelCardNextImage"
          />
        </StyledImageContainer>
      </StyledLinkBox>
      <StyledCol $fullWidth $notCenter $gap={isDesktop ? "10px" : "4px"}>
        {isPreSale && (
          <StyledPreSaleTagWrapper>
            <PreSaleTag size="small" />
          </StyledPreSaleTagWrapper>
        )}
        {!(isFinance || isshop) ? (
          <>
            <StyledTitle>{modelData?.name}</StyledTitle>

            {isPreSale ? (
              <StyledAnnouncement>הטבת השקה מיוחדת</StyledAnnouncement>
            ) : (
              isInShop && (
                <StyledAnnouncement>עכשיו בהנחת אונליין</StyledAnnouncement>
              )
            )}
            <StyledPrice>
              {futureModel
                ? `${modelData?.secondaryTitle}`
                : `החל מ-₪${modelData?.price.toLocaleString()}`}
            </StyledPrice>
            <StyledLicenseFee>
              {modelData?.licenceFee && !futureModel
                ? `בתוספת אגרת רישוי בסך ₪${modelData?.licenceFee.toLocaleString()} כולל מע"מ`
                : ""}
            </StyledLicenseFee>
            <StyledButtonRow>
              <NextLink
                href={modelData?.link || carLink}
                title={modelData?.name}
                noEncoding={futureModel}
              >
                <>
                  <Typography variant="body1" hidden>
                    {modelData?.name}
                  </Typography>

                  <StyledButton>גלו עוד</StyledButton>
                </>
              </NextLink>
              {!futureModel && isInShop && (
                <NextLink href={shopUrl} title={modelData?.name} noEncoding>
                  <>
                    <Typography variant="body1" hidden>
                      {modelData?.name}
                    </Typography>
                    <StyledButton>להזמנה</StyledButton>
                  </>
                </NextLink>
              )}
            </StyledButtonRow>
          </>
        ) : (
          <NextLink href={linkUrl} noEncoding>
            <StyledLinkBox as="a">
              <StyledTitle $left={isFinance || isshop}>
                {modelData?.name}
              </StyledTitle>
              {isshop && (
                <StyledAnnouncement>
                  {modelData?.secondaryTitle}
                </StyledAnnouncement>
              )}
              <StyledPrice $left={isFinance || isshop}>
                {`החל מ-₪${modelData?.price.toLocaleString()}`}
              </StyledPrice>
              <StyledLicenseFee $left={isFinance || isshop}>
                {modelData?.licenceFee
                  ? `בתוספת אגרת רישוי בסך ₪${modelData?.licenceFee.toLocaleString()} כולל מע"מ`
                  : ""}
              </StyledLicenseFee>
            </StyledLinkBox>
          </NextLink>
        )}
      </StyledCol>
    </StyledWrapper>
  )
}

export default ModelCard
